import { Suspense } from 'react';
import { Await, useLoaderData, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { Formik, useFormikContext } from 'formik';

import { SUCCESS_DIALOG } from '~common';
import { ErrorMessage } from '~components/atoms';
import {
  LoadingButton,
  LoadingButtonNavigation,
  PaddingBox,
} from '~components/atoms/custom';
import { TextField } from '~components/atoms/form';
import { SuccessDialogWithNavigation } from '~components/organisms';
import { FORGOT_PASSWORD_PATH, LOGIN_PATH } from '~configs';
import {
  ResetPasswordProvider,
  resetPasswordValidationSchema,
  ResetPasswordValues,
  useResetPasswordContext,
} from '~providers/ResetPasswordProvider';

export const ResetPasswordPage = () => {
  const { validatePromise } = useLoaderData() as {
    validatePromise: Promise<unknown>;
  };

  return (
    <Suspense
      fallback={
        <Box
          display="flex"
          width="100vw"
          height="100vh"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      }
    >
      <Await
        resolve={validatePromise}
        errorElement={<ResetPasswordFallback />}
        children={
          <ResetPasswordProvider>
            <ResetPasswordPageViewWrapper />
          </ResetPasswordProvider>
        }
      />
    </Suspense>
  );
};

const ResetPasswordPageViewWrapper = () => {
  const { resetPasswordValues, handleSubmit } = useResetPasswordContext();

  return (
    <Formik
      initialValues={resetPasswordValues}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={resetPasswordValidationSchema}
    >
      <ResetPasswordPageView />
    </Formik>
  );
};

const ResetPasswordPageView = () => {
  const {
    isResettingPassword,
    isSuccessPasswordResetDialogOpen,
    closeSuccessPasswordResetDialog,
  } = useResetPasswordContext();

  const { submitForm, errors, dirty } = useFormikContext<ResetPasswordValues>();

  const navigate = useNavigate();

  return (
    <>
      <PaddingBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={3}
        px={3}
        width="100%"
      >
        {/* Form Input */}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          width="100%"
        >
          {/* Error Display */}
          {errors.submit && (
            <ErrorMessage
              errorText={errors.submit}
              actionText={
                <Typography variant="body2">
                  Please click{' '}
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      cursor: 'pointer',
                      ':hover': { textDecoration: 'underline' },
                      color: '#0081CC',
                    }}
                    onClick={() => navigate(FORGOT_PASSWORD_PATH)}
                  >
                    here
                  </Typography>{' '}
                  to send the email again.
                </Typography>
              }
            />
          )}

          <Typography variant="h6" fontWeight={600}>
            Reset your password
          </Typography>
          <TextField
            label={
              <Typography variant="body2" fontWeight={400}>
                New Password
              </Typography>
            }
            name="password"
            type="password"
            autoComplete="on"
          />
          <TextField
            label={
              <Typography variant="body2" fontWeight={400}>
                Confirm Password
              </Typography>
            }
            name="confirmPassword"
            type="password"
            autoComplete="on"
          />
        </Box>

        {/* Change Password Button */}
        <LoadingButton
          onClick={submitForm}
          disabled={!dirty}
          isLoading={isResettingPassword}
        >
          <Typography variant="body2" fontWeight={500}>
            Change Password
          </Typography>
        </LoadingButton>
      </PaddingBox>

      {/* Successful reset password */}
      <SuccessDialogWithNavigation
        open={isSuccessPasswordResetDialogOpen}
        onClose={closeSuccessPasswordResetDialog}
        title={SUCCESS_DIALOG.PASSWORD_RESET.TITLE}
        subtitle={SUCCESS_DIALOG.PASSWORD_RESET.SUBTITLE}
        btnText={SUCCESS_DIALOG.PASSWORD_RESET.BUTTON_TEXT}
        path={LOGIN_PATH}
      />
    </>
  );
};

const ResetPasswordFallback: React.FC = () => {
  return (
    <PaddingBox
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
      gap={3}
    >
      <Stack gap={1} display="flex" alignItems="center">
        <Typography variant="h6" fontWeight={600}>
          Reset link has expired
        </Typography>
        <Typography color="text.placeholder" textAlign="center">
          Your email to reset your password has expired or is invalid.
        </Typography>
      </Stack>
      <LoadingButtonNavigation path={FORGOT_PASSWORD_PATH}>
        <Typography variant="body2" fontWeight={500}>
          Send new reset email
        </Typography>
      </LoadingButtonNavigation>
      <LoadingButtonNavigation variant="text" path={LOGIN_PATH} sx={{ p: 0 }}>
        <Typography variant="body2" fontWeight={500}>
          Return to login
        </Typography>
      </LoadingButtonNavigation>
    </PaddingBox>
  );
};
